import { leaveRequestStatus } from 'src/models/leave.enum';

export const CANCELLATION_IN_REVIEW_MESSAGE =
  'Leave is already in review for cancellation';

export const CANCELLATION_SUBMITTED_FOR_REVIEW_MESSAGE =
  'Cancellation request submitted for review.';

export const CANCELLATION_AUTO_CANCELLED_MESSAGE =
  'Leave cancelled successfully. Notifications have been sent.';

export const CANCELLATION_WINDOW_DAYS = 7;

export const CANCELLATION_WINDOW_CLOSED_MESSAGE =
  'Cannot cancel. More than 7 days have passed since the request date.';

const startOfDay = (value: Date) => {
  const date = new Date(value);
  date.setHours(0, 0, 0, 0);
  return date;
};

/**
 * Past and today need approve/reject (CANCELLATION_REQUESTED).
 * A leave day after today is cancelled immediately (CANCELLED).
 */
export const doesCancellationRequireApproval = (leave: any): boolean => {
  const from = leave?.fromDate;
  if (!from) {
    return leave?.cancellationRequiresApproval !== false;
  }
  const fromDate = new Date(from);
  if (Number.isNaN(fromDate.getTime())) {
    return leave?.cancellationRequiresApproval !== false;
  }
  return startOfDay(fromDate).getTime() <= startOfDay(new Date()).getTime();
};

/**
 * Cancel is allowed only through 7 days after the leave request date.
 * Request on 2026-10-08 stays open through 2026-10-15.
 */
export const isWithinCancellationWindow = (leave: any): boolean => {
  const requestRaw = leave?.leaveRequestDate || leave?.requestDate;
  if (!requestRaw) {
    return true;
  }
  const requestDate = new Date(requestRaw);
  if (Number.isNaN(requestDate.getTime())) {
    return true;
  }
  const windowEnd = startOfDay(requestDate);
  windowEnd.setDate(windowEnd.getDate() + CANCELLATION_WINDOW_DAYS);
  return startOfDay(new Date()).getTime() <= windowEnd.getTime();
};

const normalizeStatus = (leave: any): string => {
  const rawStatus =
    leave?.status ||
    leave?.leaveStatus ||
    leave?.requestStatus ||
    leave?.leaveRequestStatus ||
    '';

  return String(rawStatus)
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, '_');
};

export const isCancellationInReview = (leave: any): boolean => {
  const status = normalizeStatus(leave);

  return (
    status === leaveRequestStatus.CANCELLATION_REQUESTED ||
    status === 'CANCELLATION_PENDING' ||
    status === 'CANCELLATION_REQUEST' ||
    status.includes('CANCELLATION_REQUEST')
  );
};

/**
 * Detects cancellation-request leaves shown under Pending tabs.
 * Supports API enum codes, display labels, and cancelReason presence.
 */
export const isCancellationRequest = (leave: any): boolean => {
  if (!leave) {
    return false;
  }

  const status = normalizeStatus(leave);

  if (
    status === leaveRequestStatus.CANCELLATION_REQUESTED ||
    status === 'CANCELLATION_PENDING' ||
    status === 'CANCELLATION_REQUEST' ||
    status.includes('CANCELLATION_REQUEST')
  ) {
    return true;
  }

  const cancelReason =
    leave.cancelReason ||
    leave.cancellationReason ||
    leave.cancel_reason ||
    leave.leaveCancelReason;

  // Fallback: leave has a cancel reason and is not fully cancelled
  if (
    cancelReason &&
    status !== leaveRequestStatus.CANCELLED &&
    status !== 'CANCELLED'
  ) {
    return true;
  }

  return false;
};

export const getCancelReason = (leave: any): string => {
  if (!leave) {
    return '';
  }

  const reason =
    leave.cancelReason ||
    leave.cancellationReason ||
    leave.cancel_reason ||
    leave.leaveCancelReason ||
    '';

  return String(reason).trim();
};

export const getEmploymentType = (leave: any): string => {
  return (
    leave?.leaveRequester?.employeeFinance?.type ||
    leave?.employeeDetailsDto?.employeeFinance?.type ||
    leave?.employmentType ||
    ''
  );
};
